import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { BookOpen, PenSquare, Globe } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

type FeedbackHeaderProps = {
  company: {
    id: string;
    name: string;
    logo_url?: string | null;
    slug: string;
  };
};

const FeedbackHeader = ({ company }: FeedbackHeaderProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [session, setSession] = useState(null);

  // Check if user is authenticated on component mount
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {
        setSession(currentSession);
      }
    );

    // Initial session check
    checkSession();

    // Cleanup subscription
    return () => subscription.unsubscribe();
  }, []);

  const navigateToNewFeedback = () => {
    // Check if we're on a custom domain
    const hostname = window.location.hostname;
    
    if (hostname === 'feedback.feednatic.com') {
      // Special case for feednatic.com
      navigate('/feedback/new');
      return;
    } else if (hostname.startsWith('feedback.')) {
      // If on a custom domain, navigate to /feedback/new directly
      navigate('/feedback/new');
      return;
    }
    
    // Otherwise use the standard slug routing
    navigate(`/${company.slug}/feedback/new`);
  };

  const navigateToHelpDesk = () => {
    // Check if we're on a custom domain
    const hostname = window.location.hostname;
    
    if (hostname === 'feedback.feednatic.com') {
      // Special case for feednatic.com
      window.location.href = 'https://help.feednatic.com';
      return;
    } else if (hostname.startsWith('feedback.')) {
      // If on a custom domain, redirect to the help subdomain
      const domain = hostname.substring(9); // Remove 'feedback.'
      window.location.href = `https://help.${domain}`;
      return;
    }
    
    // Otherwise use the standard slug routing
    navigate(`/${company.slug}/help`);
  };

  return (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
      <div className="flex items-center gap-4">
        {company.logo_url ? (
          <img
            src={company.logo_url}
            alt={`${company.name} logo`}
            className="h-10 w-10 object-contain"
          />
        ) : (
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Globe className="h-6 w-6 text-primary" />
          </div>
        )}
        <div>
          <h1 className="text-2xl font-bold">{company.name}</h1>
          <p className="text-muted-foreground">Feedback Portal</p>
        </div>
      </div>
      <div className="flex items-center gap-2 w-full md:w-auto">
        <Button
          variant="outline"
          onClick={navigateToHelpDesk}
          className="flex-1 md:flex-none"
        >
          <BookOpen className="mr-2 h-4 w-4" /> Knowledge Base
        </Button>
        <Button onClick={navigateToNewFeedback} className="flex-1 md:flex-none">
          <PenSquare className="mr-2 h-4 w-4" /> New Feedback
        </Button>
      </div>
    </div>
  );
};

export default FeedbackHeader;
